/**
 * Menú QR del Comensal.
 *
 * Página pública accesible escaneando el código QR de la mesa (`/mesa/:token`).
 * No requiere autenticación. Permite explorar el menú, personalizar platillos con
 * modificadores, armar el carrito, enviar la comanda a la cocina y dar seguimiento
 * en tiempo real al estado de la cuenta.
 */
import { useEffect, useState, useMemo } from 'react';
import {
  publicApi,
  type InfoMesaPublica,
  type InfoMostradorPublica,
  type MenuPublicoRespuesta,
  type MenuMostradorRespuesta,
  type ProductoPublico,
  type CategoriaPublica,
  type GrupoModificador,
  type OpcionModificador,
  type ItemCarrito,
  type ResumenCuentaPublica,
  type OrdenMostrador,
} from '../lib/publicApi';
import { conectarSocketMesa, desconectarSocketMesa } from '../lib/socket';
import { LogoErimar } from '../componentes/LogoErimar';
import { cantidad } from '../lib/formato';
import { reproducirTonoOrdenNueva } from '../lib/audio';
import { vibrarAvisoOrdenLista } from '../lib/vibration';

interface Props {
  token: string;
  /** 'mostrador' = pedido para llevar, sin mesa: se identifica por nombre, no por número. */
  modo?: 'mesa' | 'mostrador';
}

/**
 * Los pedidos de mostrador de esta persona se guardan en su navegador. Sin
 * esto, cerrar la pestaña o que se apague la pantalla del celular mientras
 * espera basta para perder de vista si ya está listo o no.
 */
function llavePedidos(token: string) {
  return `ericmar_pedidos_mostrador_${token}`;
}

function leerIdsPedidos(token: string): number[] {
  try {
    const guardado = localStorage.getItem(llavePedidos(token));
    return guardado ? (JSON.parse(guardado) as number[]) : [];
  } catch {
    return [];
  }
}

function guardarIdsPedidos(token: string, ids: number[]) {
  try {
    // Un cliente no manda más de un puñado de pedidos en una visita; con 20
    // sobra y evita que el navegador acumule ids de visitas de hace meses.
    localStorage.setItem(llavePedidos(token), JSON.stringify(ids.slice(-20)));
  } catch {
    // Modo incógnito o storage lleno: no es motivo para romper el pedido.
  }
}

/** Texto e ícono del aviso de pantalla completa, según en qué va el pedido. */
function contenidoAviso(ped: OrdenMostrador) {
  if (ped.estado === 'en_preparacion') {
    return {
      icono: '👨‍🍳',
      titulo: '¡Ya estamos preparando tu pedido!',
      subtitulo: 'Te avisamos otra vez en cuanto esté listo.',
      color: 'bg-miel-oscuro',
      colorTexto: 'text-miel-claro',
      tituloNotif: `Preparando tu pedido #${ped.numero_orden} 👨‍🍳`,
      cuerpoNotif: `A nombre de ${ped.nombre_cliente}. Te avisamos otra vez cuando esté listo.`,
    };
  }
  return {
    icono: '🛍️✅',
    titulo: '¡Tu pedido está listo!',
    subtitulo: 'Pasa a caja a pagarlo y recogerlo.',
    color: 'bg-nopal-oscuro',
    colorTexto: 'text-nopal-claro',
    tituloNotif: '¡Tu pedido está listo! 🛍️',
    cuerpoNotif: `Pedido #${ped.numero_orden}, a nombre de ${ped.nombre_cliente}. Pasa a caja a pagar y recogerlo.`,
  };
}

export function MenuQR({ token, modo = 'mesa' }: Props) {
  const esMostrador = modo === 'mostrador';
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [infoMesa, setInfoMesa] = useState<InfoMesaPublica | null>(null);
  const [infoMostrador, setInfoMostrador] = useState<InfoMostradorPublica | null>(null);
  const [menu, setMenu] = useState<MenuPublicoRespuesta | MenuMostradorRespuesta | null>(null);

  // Sólo mostrador: nombre para identificar el pedido y los pedidos que esta
  // misma persona ya mandó en esta visita (no hay "cuenta" que los agrupe).
  const [nombreCliente, setNombreCliente] = useState('');
  const [misPedidos, setMisPedidos] = useState<OrdenMostrador[]>([]);
  const [verMisPedidos, setVerMisPedidos] = useState(false);
  // Pantalla completa cuando un pedido pasa a "lista": el aviso chiquito se
  // puede pasar por alto, éste no.
  const [avisoListo, setAvisoListo] = useState<OrdenMostrador | null>(null);
  const [permisoNotif, setPermisoNotif] = useState<NotificationPermission | 'no-soportado'>(
    typeof Notification !== 'undefined' ? Notification.permission : 'no-soportado'
  );

  // Filtros
  const [categoriaSel, setCategoriaSel] = useState<number | null>(null);
  const [busqueda, setBusqueda] = useState('');

  // Carrito y selección
  const [carrito, setCarrito] = useState<ItemCarrito[]>([]);
  const [productoSeleccionado, setProductoSeleccionado] = useState<ProductoPublico | null>(null);
  const [modificadoresEscogidos, setModificadoresEscogidos] = useState<Record<number, number>>({});
  const [notasItem, setNotasItem] = useState('');
  const [notasOrden, setNotasOrden] = useState('');

  // Modales / Drawers
  const [verCarrito, setVerCarrito] = useState(false);
  const [verCuenta, setVerCuenta] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [cuenta, setCuenta] = useState<ResumenCuentaPublica | null>(null);
  const [mensajeExito, setMensajeExito] = useState<string | null>(null);

  /**
   * Aplica el estado más reciente de un pedido propio y dispara el aviso
   * (sonido, vibración, pantalla completa, notificación del sistema) sólo si
   * de verdad cambió — para no repetirlo cada vez que se revalida por
   * `sincronizar` o por el sondeo de respaldo un pedido que ya se avisó.
   */
  function aplicarActualizacionPedido(anterior: string | undefined, actualizada: OrdenMostrador) {
    setMisPedidos((ps) => ps.map((p) => (p.id === actualizada.id ? actualizada : p)));

    if (anterior === actualizada.estado) return;
    setMensajeExito(`¡Tu pedido #${actualizada.numero_orden} cambió a: ${actualizada.estado.replace('_', ' ')}!`);

    // Cada paso se avisa, no sólo "listo": que sepa que ya lo vieron
    // en cocina es tan importante como saber que ya puede pasar.
    if (actualizada.estado === 'en_preparacion' || actualizada.estado === 'lista') {
      reproducirTonoOrdenNueva();
      vibrarAvisoOrdenLista();
      setAvisoListo(actualizada);

      // Si tiene la pantalla apagada o cambió de app, un mensaje
      // chiquito en una pestaña de fondo no lo va a ver a tiempo.
      // Se lee el permiso del navegador directo (no el estado de
      // React) para no depender de si el efecto se volvió a armar.
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.hidden) {
        const { tituloNotif, cuerpoNotif } = contenidoAviso(actualizada);
        try {
          new Notification(tituloNotif, { body: cuerpoNotif, tag: `pedido-${actualizada.id}` });
        } catch {
          // Algunos navegadores en Android exigen Service Worker
          // para notificaciones en segundo plano; sin eso, se
          // ignora y el aviso de pantalla completa sigue sirviendo
          // apenas la persona vuelva a la pestaña.
        }
      }
    }
  }

  // Carga inicial y reconexión por Socket
  useEffect(() => {
    let cancelado = false;

    async function cargarTodo() {
      try {
        setCargando(true);
        setError(null);

        if (esMostrador) {
          const [mostradorData, menuData] = await Promise.all([
            publicApi.obtenerMostrador(token),
            publicApi.obtenerMenuMostrador(token),
          ]);
          if (cancelado) return;
          setInfoMostrador(mostradorData);
          setMenu(menuData);

          // Recuperar los pedidos que esta persona ya había mandado, por si
          // cerró el navegador o se le apagó la pantalla mientras esperaba.
          const idsGuardados = leerIdsPedidos(token);
          if (idsGuardados.length > 0) {
            const recuperados = await Promise.all(
              idsGuardados.map((id) => publicApi.obtenerOrdenMostrador(token, id).catch(() => null))
            );
            if (!cancelado) {
              setMisPedidos(recuperados.filter((p): p is OrdenMostrador => p !== null));
            }
          }
        } else {
          const [mesaData, menuData] = await Promise.all([
            publicApi.obtenerMesa(token),
            publicApi.obtenerMenu(token),
          ]);
          if (cancelado) return;
          setInfoMesa(mesaData);
          setMenu(menuData);

          // Si tiene cuenta activa, cargar consumo acumulado
          if (mesaData.cuentaActivaId) {
            const resCuenta = await publicApi.obtenerCuenta(token).catch(() => null);
            if (resCuenta) setCuenta(resCuenta);
          }
        }
      } catch (err: any) {
        if (!cancelado) setError(err?.message || 'No se pudo cargar el menú.');
      } finally {
        if (!cancelado) setCargando(false);
      }
    }

    cargarTodo();

    // Conectar a la sala de socket de esta mesa (o del mostrador: comparte
    // el mismo mecanismo de sala, el token trae el número reservado 0).
    const socket = conectarSocketMesa(token);

    socket.on('orden:cambio', (evt: any) => {
      if (esMostrador) {
        // El mostrador comparte un solo QR entre todos los clientes: sólo
        // reaccionamos si el cambio es de UN pedido que esta persona mandó.
        setMisPedidos((prev) => {
          const propio = prev.find((p) => p.id === evt.ordenId);
          if (!propio) return prev;
          publicApi
            .obtenerOrdenMostrador(token, evt.ordenId)
            .then((actualizada) => aplicarActualizacionPedido(propio.estado, actualizada))
            .catch(() => null);
          return prev;
        });
        return;
      }
      setMensajeExito(`¡Tu orden #${evt.numeroOrden || ''} cambió a estado: ${evt.estadoNuevo}!`);
      publicApi.obtenerCuenta(token).then(setCuenta).catch(() => null);
    });

    socket.on('catalogo:cambio', () => {
      if (esMostrador) {
        publicApi.obtenerMenuMostrador(token).then(setMenu).catch(() => null);
      } else {
        publicApi.obtenerMenu(token).then(setMenu).catch(() => null);
      }
    });

    // El servidor manda esto al conectar y CADA VEZ que reconecta. Un
    // celular en el bolsillo pierde la conexión constantemente (pantalla
    // bloqueada, cambia de red); sin esto, un cambio de estado que pasó
    // mientras estuvo desconectado se perdía para siempre, sin aviso.
    socket.on('sincronizar', () => {
      if (!esMostrador) return;
      setMisPedidos((prev) => {
        Promise.all(
          prev.map((p) =>
            publicApi
              .obtenerOrdenMostrador(token, p.id)
              .then((actualizada) => aplicarActualizacionPedido(p.estado, actualizada))
              .catch(() => null)
          )
        );
        return prev;
      });
    });

    return () => {
      cancelado = true;
      desconectarSocketMesa();
    };
  }, [token, esMostrador]);

  // Guarda la lista de pedidos propios cada vez que cambia, para sobrevivir
  // a que cierre la pestaña o se le apague la pantalla.
  useEffect(() => {
    if (!esMostrador) return;
    const terminada = misPedidos.length > 0 && misPedidos.every((p) => p.estado === 'entregada' || p.estado === 'cancelada');
    // Al terminar la visita se borra lo guardado (no lo que ya está en
    // pantalla, sólo lo que se recuperaría en una carga nueva): así, si de
    // verdad vuelve a escanear el mismo QR en el mismo celular, empieza de
    // cero en vez de quedarse trabado viendo el aviso de "gracias" para
    // siempre. Esta pestaña sigue mostrándolo hasta que la cierre o recargue.
    guardarIdsPedidos(token, terminada ? [] : misPedidos.map((p) => p.id));
  }, [token, esMostrador, misPedidos]);

  // Red de respaldo: en un celular real, el WiFi/datos se cae y el socket se
  // reconecta todo el tiempo sin avisar (pantalla bloqueada, cambia de red,
  // ahorro de batería). Preguntar por HTTP cada 15s no depende de nada de
  // eso — el aviso llega sí o sí, aunque el socket ese día ande fallando.
  useEffect(() => {
    if (!esMostrador) return;
    const pendientes = misPedidos.some((p) => p.estado !== 'entregada' && p.estado !== 'cancelada');
    if (!pendientes) return;

    const intervalo = setInterval(() => {
      setMisPedidos((prev) => {
        for (const p of prev) {
          if (p.estado === 'entregada' || p.estado === 'cancelada') continue;
          publicApi
            .obtenerOrdenMostrador(token, p.id)
            .then((actualizada) => aplicarActualizacionPedido(p.estado, actualizada))
            .catch(() => null);
        }
        return prev;
      });
    }, 15000);

    return () => clearInterval(intervalo);
  }, [token, esMostrador, misPedidos]);

  function pedirPermisoNotificaciones() {
    if (typeof Notification === 'undefined') return;
    Notification.requestPermission().then(setPermisoNotif);
  }

  // Filtrado de productos por categoría y búsqueda
  const productosFiltrados = useMemo(() => {
    if (!menu) return [];
    return menu.productos.filter((p) => {
      const coincideCat = categoriaSel === null || p.categoria_id === categoriaSel;
      const q = busqueda.trim().toLowerCase();
      const coincideBusqueda = !q || p.nombre.toLowerCase().includes(q) || (p.descripcion && p.descripcion.toLowerCase().includes(q));
      return coincideCat && coincideBusqueda;
    });
  }, [menu, categoriaSel, busqueda]);

  // Abrir modal para configurar modificadores o agregar directo
  function iniciarAgregar(producto: ProductoPublico) {
    const gruposProd = obtenerGruposDeProducto(producto);

    if (gruposProd.length > 0) {
      setProductoSeleccionado(producto);
      setNotasItem('');
      // Pre-seleccionar primera opción para grupos obligatorios de tipo 'una'
      const iniciales: Record<number, number> = {};
      for (const g of gruposProd) {
        if (g.obligatorio && g.opciones.length > 0) {
          iniciales[g.id] = g.opciones[0].id;
        }
      }
      setModificadoresEscogidos(iniciales);
    } else {
      agregarAlCarrito(producto, [], '');
    }
  }

  function obtenerGruposDeProducto(prod: ProductoPublico): GrupoModificador[] {
    if (prod.modificadores && prod.modificadores.length > 0) return prod.modificadores;
    if (!menu) return [];
    // Si no vienen ligados en el objeto, buscar todos los modificadores disponibles
    return menu.modificadores;
  }

  function confirmarAgregarModal() {
    if (!productoSeleccionado) return;
    const grupos = obtenerGruposDeProducto(productoSeleccionado);

    // Validar obligatorios
    for (const g of grupos) {
      if (g.obligatorio && !modificadoresEscogidos[g.id]) {
        alert(`Selecciona una opción para "${g.nombre}"`);
        return;
      }
    }

    const opcionesElegidas: OpcionModificador[] = [];
    for (const g of grupos) {
      const opcId = modificadoresEscogidos[g.id];
      if (opcId) {
        const opc = g.opciones.find((o) => o.id === opcId);
        if (opc) opcionesElegidas.push(opc);
      }
    }

    agregarAlCarrito(productoSeleccionado, opcionesElegidas, notasItem);
    setProductoSeleccionado(null);
  }

  function agregarAlCarrito(producto: ProductoPublico, opciones: OpcionModificador[], notas: string) {
    const precioBase = Number(producto.precio);
    const extras = opciones.reduce((sum, o) => sum + Number(o.precio_extra || 0), 0);
    const subtotalUnitario = precioBase + extras;

    setCarrito((prev) => {
      // Buscar si ya existe la misma variante
      const indice = prev.findIndex(
        (i) =>
          i.producto.id === producto.id &&
          i.notas === notas &&
          JSON.stringify(i.opcionesModificadores.map((o) => o.id).sort()) ===
            JSON.stringify(opciones.map((o) => o.id).sort())
      );

      if (indice >= 0) {
        const copia = [...prev];
        const prevItem = copia[indice];
        const nuevaCant = prevItem.cantidad + 1;
        copia[indice] = {
          ...prevItem,
          cantidad: nuevaCant,
          subtotal: Math.round(subtotalUnitario * nuevaCant * 100) / 100,
        };
        return copia;
      }

      return [
        ...prev,
        {
          producto,
          cantidad: 1,
          notas,
          opcionesModificadores: opciones,
          subtotal: Math.round(subtotalUnitario * 100) / 100,
        },
      ];
    });

    setMensajeExito(`"${producto.nombre}" agregado al pedido`);
    setTimeout(() => setMensajeExito(null), 3000);
  }

  function cambiarCantidad(index: number, delta: number) {
    setCarrito((prev) => {
      const copia = [...prev];
      const item = copia[index];
      const nuevaCant = item.cantidad + delta;
      if (nuevaCant <= 0) {
        return prev.filter((_, i) => i !== index);
      }
      const unitario = item.subtotal / item.cantidad;
      copia[index] = {
        ...item,
        cantidad: nuevaCant,
        subtotal: Math.round(unitario * nuevaCant * 100) / 100,
      };
      return copia;
    });
  }

  const totalCarrito = useMemo(
    () => carrito.reduce((sum, i) => sum + i.subtotal, 0),
    [carrito]
  );
  const itemsTotalesCarrito = useMemo(
    () => carrito.reduce((sum, i) => sum + i.cantidad, 0),
    [carrito]
  );

  async function enviarPedidoFinal() {
    if (carrito.length === 0 || enviando) return;
    if (esMostrador && !nombreCliente.trim()) {
      alert('Escribe tu nombre para identificar el pedido.');
      return;
    }
    try {
      setEnviando(true);
      const itemsEnvio = carrito.map((i) => ({
        productoId: i.producto.id,
        cantidad: i.cantidad,
        notas: i.notas || undefined,
        modificadores: i.opcionesModificadores.map((o) => ({
          grupoId: o.grupo_id,
          opcionId: o.id,
        })),
      }));

      if (esMostrador) {
        const orden = await publicApi.enviarOrdenMostrador(token, nombreCliente, itemsEnvio, notasOrden);
        setMisPedidos((prev) => [...prev, orden]);
        setCarrito([]);
        setNotasOrden('');
        setVerCarrito(false);
        setMensajeExito(`¡Pedido #${orden.numero_orden} enviado a cocina! 🍳 No cierres esta pantalla: así te avisamos cuando esté listo.`);
        setVerMisPedidos(true);
      } else {
        await publicApi.enviarOrden(token, itemsEnvio, notasOrden);

        setCarrito([]);
        setNotasOrden('');
        setVerCarrito(false);
        setMensajeExito('¡Tu pedido ha sido enviado a la cocina! 🍳');

        // Actualizar cuenta
        const resCuenta = await publicApi.obtenerCuenta(token).catch(() => null);
        if (resCuenta) setCuenta(resCuenta);
        setVerCuenta(true);
      }
    } catch (err: any) {
      alert(err?.message || 'No se pudo enviar el pedido. Intenta de nuevo.');
    } finally {
      setEnviando(false);
    }
  }

  if (cargando) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 text-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-nopal border-t-transparent"></div>
        <p className="mt-4 font-semibold text-salvia">Cargando menú de la mesa…</p>
      </div>
    );
  }

  if (error || (esMostrador ? !infoMostrador : !infoMesa)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 text-center">
        <div className="rounded-2xl border border-barro/30 bg-barro-claro p-6 text-barro-oscuro">
          <h2 className="font-titulo text-xl font-bold">Código QR no válido</h2>
          <p className="mt-2 text-sm">{error || 'Este código no existe o fue deshabilitado.'}</p>
        </div>
      </div>
    );
  }

  // El mostrador es un solo QR fijo compartido por todos: no se puede
  // "caducar" el código en sí sin dejar a los demás clientes sin poder
  // pedir. Lo que sí caduca es ESTA visita: en cuanto caja cobra el pedido,
  // se acabó lo que se traía — para pedir otra vez hay que volver a
  // escanear, no seguir usando la misma pestaña abierta indefinidamente.
  const visitaTerminada =
    esMostrador && misPedidos.length > 0 && misPedidos.every((p) => p.estado === 'entregada' || p.estado === 'cancelada');

  if (visitaTerminada) {
    const ultimoPagado = [...misPedidos].reverse().find((p) => p.estado === 'entregada');
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-nopal-oscuro p-6 text-center text-white">
        <LogoErimar variant="compacta" className="brightness-0 invert" />
        <div className="text-7xl">🙌</div>
        <div>
          <h2 className="font-titulo text-2xl font-extrabold">¡Gracias por tu compra!</h2>
          {ultimoPagado && (
            <p className="mt-2 text-base">
              Pedido <span className="font-dato font-bold">#{ultimoPagado.numero_orden}</span> entregado y pagado.
            </p>
          )}
          <p className="mt-4 text-sm text-nopal-claro">
            Este código ya cumplió su función. Para hacer un nuevo pedido, vuelve a escanear el QR del mostrador.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cal pb-28">
      {/* Cabecera Móvil */}
      <header className="sticky top-0 z-20 border-b border-salvia/20 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-lg items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <LogoErimar variant="compacta" />
            <span className="h-8 w-px bg-salvia/20" aria-hidden="true" />
            <h1 className="font-titulo text-lg font-extrabold text-tinta">
              {esMostrador ? 'Para llevar' : infoMesa!.mesaNombre || `Mesa ${infoMesa!.mesaNumero}`}
            </h1>
          </div>
          {esMostrador ? (
            misPedidos.length > 0 && (
              <button
                onClick={() => setVerMisPedidos(true)}
                className="flex items-center gap-1.5 rounded-full border border-nopal/30 bg-nopal-claro px-3 py-1.5 text-xs font-bold text-nopal-oscuro"
              >
                <span>📋 Mis pedidos ({misPedidos.length})</span>
              </button>
            )
          ) : (
            cuenta && cuenta.ordenes && cuenta.ordenes.length > 0 && (
              <button
                onClick={() => setVerCuenta(true)}
                className="flex items-center gap-1.5 rounded-full border border-nopal/30 bg-nopal-claro px-3 py-1.5 text-xs font-bold text-nopal-oscuro"
              >
                <span>📋 Ver mi comanda</span>
              </button>
            )
          )}
        </div>

        {/* Aviso: el aviso de "listo" depende de tener esta pestaña abierta */}
        {esMostrador && misPedidos.some((p) => p.estado !== 'entregada' && p.estado !== 'cancelada') && (
          <div className="mx-auto flex max-w-lg items-center justify-between gap-2 border-t border-miel/30 bg-miel/10 px-4 py-2 text-xs text-miel-oscuro">
            <span>
              ⚠️ No cierres esta pantalla: así te avisamos en cuanto tu pedido esté listo. Si la cierras, no
              recibirás la notificación.
            </span>
            {permisoNotif === 'default' && (
              <button
                onClick={pedirPermisoNotificaciones}
                className="shrink-0 whitespace-nowrap rounded-full border border-miel/50 bg-white px-2.5 py-1 text-[11px] font-bold text-miel-oscuro"
              >
                🔔 Activar
              </button>
            )}
          </div>
        )}

        {/* Buscador */}
        <div className="mx-auto max-w-lg px-4 pb-2">
          <input
            type="search"
            placeholder="🔍 Buscar platillo o bebida…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full rounded-xl border border-salvia/25 bg-cal px-3.5 py-2 text-sm focus:border-nopal focus:bg-white"
          />
        </div>

        {/* Pestañas de Categorías */}
        {menu && menu.categorias.length > 0 && (
          <div className="flex overflow-x-auto px-4 pb-3 scrollbar-none">
            <button
              onClick={() => setCategoriaSel(null)}
              className={`mr-2 whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-bold transition ${
                categoriaSel === null
                  ? 'bg-nopal text-white shadow-sm'
                  : 'bg-cal text-salvia hover:bg-salvia/10'
              }`}
            >
              Todos los productos
            </button>
            {menu.categorias.map((cat: CategoriaPublica) => (
              <button
                key={cat.id}
                onClick={() => setCategoriaSel(cat.id)}
                className={`mr-2 whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-bold transition ${
                  categoriaSel === cat.id
                    ? 'bg-nopal text-white shadow-sm'
                    : 'bg-cal text-salvia hover:bg-salvia/10'
                }`}
              >
                {cat.nombre}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* Alerta flotante de mensaje */}
      {mensajeExito && (
        <div className="sticky top-24 z-30 mx-auto max-w-md px-4">
          <div className="rounded-xl border border-nopal/30 bg-nopal-claro px-4 py-2.5 text-center text-sm font-bold text-nopal-oscuro shadow-md">
            {mensajeExito}
          </div>
        </div>
      )}

      {/* Grid de Productos */}
      <main className="mx-auto max-w-lg px-4 pt-4">
        {productosFiltrados.length === 0 ? (
          <div className="py-16 text-center text-salvia">
            <p className="text-lg">No hay productos en esta categoría.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            {productosFiltrados.map((prod) => (
              <div
                key={prod.id}
                className="flex flex-col justify-between rounded-2xl border border-salvia/20 bg-white p-3.5 shadow-sm transition hover:shadow-md"
              >
                <div>
                  {prod.imagen_url && (
                    <img
                      src={prod.imagen_url}
                      alt={prod.nombre}
                      className="mb-2.5 h-36 w-full rounded-xl object-cover"
                    />
                  )}
                  <h3 className="font-titulo text-base font-bold text-tinta">{prod.nombre}</h3>
                  {prod.descripcion && (
                    <p className="mt-1 text-xs text-salvia line-clamp-2">{prod.descripcion}</p>
                  )}
                </div>

                <div className="mt-3 flex items-center justify-between border-t border-salvia/10 pt-2.5">
                  <span className="font-titulo text-lg font-extrabold text-nopal-oscuro">
                    ${Number(prod.precio).toFixed(2)}
                  </span>
                  <button
                    onClick={() => iniciarAgregar(prod)}
                    className="flex items-center gap-1 rounded-xl bg-nopal px-3.5 py-1.5 text-xs font-bold text-white transition active:scale-95 hover:bg-nopal-oscuro"
                  >
                    <span>+ Agregar</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Modal de Configuración de Modificadores */}
      {productoSeleccionado && (
        <div className="fixed inset-0 z-40 flex items-end justify-center bg-tinta/50 p-0 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="w-full max-w-md rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl">
            <div className="flex items-center justify-between border-b border-salvia/15 pb-3">
              <h2 className="font-titulo text-lg font-bold text-tinta">
                {productoSeleccionado.nombre}
              </h2>
              <button
                onClick={() => setProductoSeleccionado(null)}
                className="rounded-full p-1 text-salvia hover:bg-cal"
              >
                ✕
              </button>
            </div>

            <div className="my-4 max-h-[60vh] overflow-y-auto pr-1">
              {obtenerGruposDeProducto(productoSeleccionado).map((grupo) => (
                <div key={grupo.id} className="mb-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-titulo text-sm font-bold text-tinta">
                      {grupo.nombre}
                    </span>
                    {grupo.obligatorio === 1 && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-barro">
                        Obligatorio
                      </span>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {grupo.opciones.map((opc) => (
                      <label
                        key={opc.id}
                        className={`flex items-center justify-between rounded-xl border p-3 text-sm transition cursor-pointer ${
                          modificadoresEscogidos[grupo.id] === opc.id
                            ? 'border-nopal bg-nopal-claro text-nopal-oscuro font-semibold'
                            : 'border-salvia/20 bg-white text-tinta'
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <input
                            type="radio"
                            name={`grupo-${grupo.id}`}
                            checked={modificadoresEscogidos[grupo.id] === opc.id}
                            onChange={() =>
                              setModificadoresEscogidos((prev) => ({
                                ...prev,
                                [grupo.id]: opc.id,
                              }))
                            }
                            className="accent-nopal"
                          />
                          <span>{opc.nombre}</span>
                        </div>
                        {Number(opc.precio_extra) > 0 && (
                          <span className="text-xs font-bold text-nopal-oscuro">
                            +${Number(opc.precio_extra).toFixed(2)}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              <div className="mt-4">
                <label className="mb-1 block text-xs font-bold text-salvia">
                  Notas de preparación (opcional)
                </label>
                <input
                  type="text"
                  placeholder="Ej. Sin cebolla, extra salsa…"
                  value={notasItem}
                  onChange={(e) => setNotasItem(e.target.value)}
                  className="w-full rounded-xl border border-salvia/30 px-3 py-2 text-sm focus:border-nopal"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setProductoSeleccionado(null)}
                className="flex-1 rounded-xl border border-salvia/30 py-3 text-sm font-bold text-salvia hover:bg-cal"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarAgregarModal}
                className="flex-1 rounded-xl bg-nopal py-3 text-sm font-bold text-white hover:bg-nopal-oscuro"
              >
                Agregar al pedido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drawer / Modal del Carrito */}
      {verCarrito && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-tinta/60 p-0 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="w-full max-w-md rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl">
            <div className="flex items-center justify-between border-b border-salvia/15 pb-3">
              <h2 className="font-titulo text-xl font-bold text-tinta">Mi Pedido</h2>
              <button onClick={() => setVerCarrito(false)} className="text-salvia hover:bg-cal p-1 rounded-full">
                ✕
              </button>
            </div>

            <div className="my-4 max-h-[50vh] overflow-y-auto space-y-3 pr-1">
              {carrito.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between rounded-xl border border-salvia/15 bg-cal/50 p-3">
                  <div className="flex-1 pr-2">
                    <h4 className="font-titulo text-sm font-bold text-tinta">{item.producto.nombre}</h4>
                    {item.opcionesModificadores.length > 0 && (
                      <p className="text-xs text-salvia">
                        {item.opcionesModificadores.map((o) => o.nombre).join(', ')}
                      </p>
                    )}
                    {item.notas && <p className="text-xs italic text-salvia">Nota: "{item.notas}"</p>}
                    <span className="font-titulo text-xs font-bold text-nopal-oscuro">
                      ${item.subtotal.toFixed(2)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => cambiarCantidad(idx, -1)}
                      className="h-7 w-7 rounded-lg border border-salvia/30 font-bold text-tinta hover:bg-white"
                    >
                      -
                    </button>
                    <span className="w-5 text-center text-sm font-bold">{item.cantidad}</span>
                    <button
                      onClick={() => cambiarCantidad(idx, 1)}
                      className="h-7 w-7 rounded-lg border border-salvia/30 font-bold text-tinta hover:bg-white"
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}

              {esMostrador && (
                <div className="mt-4">
                  <label className="mb-1 block text-xs font-bold text-salvia">¿A nombre de quién? *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej. Juan"
                    value={nombreCliente}
                    onChange={(e) => setNombreCliente(e.target.value)}
                    className="w-full rounded-xl border border-salvia/30 px-3 py-2 text-sm focus:border-nopal"
                  />
                  <span className="mt-1 block text-xs text-salvia">
                    Así te llamamos cuando esté listo para llevar.
                  </span>
                </div>
              )}

              <div className="mt-4">
                <label className="mb-1 block text-xs font-bold text-salvia">
                  {esMostrador ? 'Notas del pedido (opcional)' : 'Notas generales para la mesa (opcional)'}
                </label>
                <input
                  type="text"
                  placeholder="Ej. Favor de traer servilletas extras…"
                  value={notasOrden}
                  onChange={(e) => setNotasOrden(e.target.value)}
                  className="w-full rounded-xl border border-salvia/30 px-3 py-2 text-sm focus:border-nopal"
                />
              </div>
            </div>

            <div className="border-t border-salvia/15 pt-3">
              <div className="mb-4 flex items-center justify-between">
                <span className="font-titulo text-base font-bold text-salvia">Total del pedido</span>
                <span className="font-titulo text-2xl font-extrabold text-nopal-oscuro">
                  ${totalCarrito.toFixed(2)}
                </span>
              </div>
              <button
                disabled={enviando || carrito.length === 0 || (esMostrador && !nombreCliente.trim())}
                onClick={enviarPedidoFinal}
                className="w-full rounded-xl bg-nopal py-3.5 font-titulo text-base font-bold text-white transition hover:bg-nopal-oscuro disabled:opacity-50"
              >
                {enviando ? 'Enviando a cocina…' : ' Confirmar y Enviar a Cocina'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drawer de Estado de Cuenta y Comandas */}
      {verCuenta && cuenta && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-tinta/60 p-0 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="w-full max-w-md rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl">
            <div className="flex items-center justify-between border-b border-salvia/15 pb-3">
              <div>
                <h2 className="font-titulo text-xl font-bold text-tinta">Comanda de la Mesa</h2>
                <p className="text-xs text-salvia">Pedidos enviados en esta visita</p>
              </div>
              <button onClick={() => setVerCuenta(false)} className="text-salvia hover:bg-cal p-1 rounded-full">
                ✕
              </button>
            </div>

            <div className="my-4 max-h-[55vh] overflow-y-auto space-y-4 pr-1">
              {(!cuenta.ordenes || cuenta.ordenes.length === 0) ? (
                <p className="py-8 text-center text-sm text-salvia">No se han enviado pedidos aún.</p>
              ) : (
                cuenta.ordenes.map((ord) => (
                  <div key={ord.id} className="rounded-2xl border border-salvia/20 bg-cal/40 p-4">
                    <div className="mb-2 flex items-center justify-between border-b border-salvia/10 pb-2">
                      <span className="font-titulo text-sm font-bold text-tinta">
                        Orden #{ord.numero_orden}
                      </span>
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-xs font-bold capitalize ${
                          ord.estado === 'recibida'
                            ? 'bg-miel/20 text-miel-oscuro'
                            : ord.estado === 'en_preparacion'
                            ? 'bg-miel text-white'
                            : ord.estado === 'lista'
                            ? 'bg-nopal text-white'
                            : 'bg-salvia/20 text-tinta'
                        }`}
                      >
                        {ord.estado.replace('_', ' ')}
                      </span>
                    </div>

                    <div className="space-y-1.5">
                      {ord.detalles.map((det) => (
                        <div key={det.id} className="flex justify-between text-xs">
                          <div>
                            <span className="font-bold text-tinta">{cantidad(det.cantidad)}x </span>
                            <span>{det.producto_nombre}</span>
                            {det.modificadores && det.modificadores.length > 0 && (
                              <p className="text-[10px] text-salvia">
                                {det.modificadores.map((m) => m.nombre_opcion).join(', ')}
                              </p>
                            )}
                          </div>
                          <span className="font-semibold text-nopal-oscuro">
                            ${Number(det.subtotal).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="border-t border-salvia/15 pt-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-titulo text-base font-bold text-salvia">Consumo total acumulado</span>
                <span className="font-titulo text-2xl font-extrabold text-nopal-oscuro">
                  ${Number(cuenta.total || 0).toFixed(2)}
                </span>
              </div>

              {cuenta.id && (
                <button
                  onClick={() => {
                    const rfc = prompt('Ingresa tu RFC:');
                    if (!rfc) return;
                    const razonSocial = prompt('Ingresa tu Razón Social / Nombre:');
                    if (!razonSocial) return;
                    const codigoPostal = prompt('Ingresa tu Código Postal (5 dígitos):');
                    if (!codigoPostal) return;
                    const regimenFiscal = prompt('Ingresa tu Régimen Fiscal (ej. 601, 612, 605):') || '601';

                    publicApi.solicitarFactura({
                      sucursalId: infoMesa!.sucursalId,
                      cuentaMesaId: Number(cuenta.id),
                      receptor: { rfc, razonSocial, codigoPostal, regimenFiscal, usoCfdi: 'G03' },
                    }).then((res) => {
                      alert(res?.mensaje || 'Factura solicitada correctamente.');
                    }).catch((err) => alert(err?.message || 'Error al solicitar factura.'));
                  }}
                  className="w-full rounded-xl border border-nopal/30 bg-nopal-claro py-2.5 text-xs font-bold text-nopal-oscuro hover:bg-nopal/20 transition"
                >
                  📄 Necesito Factura CFDI 4.0
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Drawer de Pedidos de Mostrador (para llevar) */}
      {verMisPedidos && esMostrador && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-tinta/60 p-0 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="w-full max-w-md rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl">
            <div className="flex items-center justify-between border-b border-salvia/15 pb-3">
              <div>
                <h2 className="font-titulo text-xl font-bold text-tinta">Mis Pedidos</h2>
                <p className="text-xs text-salvia">Para llevar · {infoMostrador?.sucursalNombre}</p>
              </div>
              <button onClick={() => setVerMisPedidos(false)} className="text-salvia hover:bg-cal p-1 rounded-full">
                ✕
              </button>
            </div>

            <div className="my-4 max-h-[60vh] overflow-y-auto space-y-4 pr-1">
              {misPedidos.length === 0 ? (
                <p className="py-8 text-center text-sm text-salvia">Todavía no has mandado ningún pedido.</p>
              ) : (
                misPedidos.map((ped) => (
                  <div key={ped.id} className="rounded-2xl border border-salvia/20 bg-cal/40 p-4">
                    <div className="mb-2 flex items-center justify-between border-b border-salvia/10 pb-2">
                      <div>
                        <span className="font-titulo text-sm font-bold text-tinta">
                          Pedido #{ped.numero_orden}
                        </span>
                        <p className="text-xs text-salvia">A nombre de {ped.nombre_cliente}</p>
                      </div>
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-xs font-bold capitalize ${
                          ped.estado === 'recibida'
                            ? 'bg-miel/20 text-miel-oscuro'
                            : ped.estado === 'en_preparacion'
                            ? 'bg-miel text-white'
                            : ped.estado === 'lista'
                            ? 'bg-nopal text-white'
                            : ped.estado === 'entregada'
                            ? 'bg-salvia/20 text-tinta'
                            : 'bg-barro/20 text-barro-oscuro'
                        }`}
                      >
                        {ped.estado === 'lista' ? '¡Listo para recoger!' : ped.estado.replace('_', ' ')}
                      </span>
                    </div>

                    <div className="space-y-1.5">
                      {ped.detalles.map((det) => (
                        <div key={det.id} className="flex justify-between text-xs">
                          <span>
                            <span className="font-bold text-tinta">{cantidad(det.cantidad)}x </span>
                            {det.producto_nombre}
                          </span>
                          <span className="font-semibold text-nopal-oscuro">${Number(det.subtotal).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-2 border-t border-salvia/10 pt-2 text-right">
                      <span className="font-titulo text-base font-extrabold text-nopal-oscuro">
                        ${Number(ped.total).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {misPedidos.some((p) => p.estado !== 'entregada' && p.estado !== 'cancelada') && (
              <p className="mb-2 rounded-xl bg-miel/10 px-3 py-2 text-center text-xs font-semibold text-miel-oscuro">
                ⚠️ No cierres esta pantalla ni la pestaña: es lo que nos permite avisarte cuando esté listo.
              </p>
            )}
            <p className="text-center text-xs text-salvia">
              Cuando esté listo, pasa a caja a pagarlo y recogerlo.
            </p>
          </div>
        </div>
      )}

      {/* Aviso de pantalla completa: cada cambio de estado del pedido */}
      {avisoListo && (
        <div
          className={`fixed inset-0 z-[60] flex flex-col items-center justify-center gap-6 p-6 text-center text-white ${
            contenidoAviso(avisoListo).color
          }`}
        >
          <div className="text-7xl">{contenidoAviso(avisoListo).icono}</div>
          <div>
            <h2 className="font-titulo text-3xl font-extrabold">{contenidoAviso(avisoListo).titulo}</h2>
            <p className="mt-2 text-lg">
              Pedido <span className="font-dato font-bold">#{avisoListo.numero_orden}</span>, a nombre de{' '}
              <span className="font-bold">{avisoListo.nombre_cliente}</span>
            </p>
            <p className={`mt-4 text-base ${contenidoAviso(avisoListo).colorTexto}`}>
              {contenidoAviso(avisoListo).subtitulo}
            </p>
          </div>
          <button
            onClick={() => setAvisoListo(null)}
            className="rounded-2xl bg-white px-8 py-3.5 font-titulo text-lg font-bold text-tinta shadow-lg active:scale-95"
          >
            {avisoListo.estado === 'lista' ? 'Entendido, voy a caja' : 'Entendido'}
          </button>
        </div>
      )}

      {/* Barra Flotante Inferior de Carrito */}
      {carrito.length > 0 && (
        <div className="fixed bottom-4 inset-x-4 z-30 mx-auto max-w-md">
          <button
            onClick={() => setVerCarrito(true)}
            className="flex w-full items-center justify-between rounded-2xl bg-nopal p-4 font-titulo text-white shadow-xl transition active:scale-[0.98] hover:bg-nopal-oscuro"
          >
            <div className="flex items-center gap-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-xs font-bold text-nopal-oscuro">
                {itemsTotalesCarrito}
              </span>
              <span className="font-bold">Ver pedido</span>
            </div>
            <span className="text-lg font-extrabold">${totalCarrito.toFixed(2)}</span>
          </button>
        </div>
      )}
    </div>
  );
}
